import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { Home, LogIn } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/hooks/use-theme";
import { useToast } from "@/hooks/use-toast";
import { appLogoSrc } from "@/lib/restaurant-brand";
import { Eye, EyeOff } from "lucide-react";

// const DEMO_ACCOUNTS = [
//   { username: "admin", role: "Admin" },
//   { username: "manager1", role: "Manager" },
//   { username: "cashier1", role: "Cashier" },
//   { username: "kitchen1", role: "Kitchen" },
//   { username: "inventory1", role: "Inventory" },
// ];

export default function Login() {
  const { user, login, defaultRoute } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    if (user) setLocation(defaultRoute);
  }, [user, defaultRoute, setLocation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(username.trim(), password);
      toast({ title: "Welcome back" });
    } catch (err) {
      toast({
        title: "Login failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background text-foreground flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto flex h-32 w-32 items-center justify-center">
            <img
              src={appLogoSrc(theme)}
              alt="Butt Karahi"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-foreground text-sm">Sign in with your account</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-border rounded-2xl p-6 space-y-4 shadow-xl"
        >
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Username
            </label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1.5 w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-secondary border border-border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white font-semibold rounded-xl py-3 hover:opacity-90 disabled:opacity-60"
          >
            <LogIn className="w-4 h-4" />
            {isSubmitting ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <Link
          href="/"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-card py-3 text-sm font-semibold text-foreground transition hover:bg-secondary"
        >
          <Home className="h-4 w-4" />
          Back to Home
        </Link>

        {/* <div className="mt-6 rounded-2xl border border-border bg-card/50 p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Demo accounts (password: password)
          </p>
          <div className="grid grid-cols-2 gap-2">
            {DEMO_ACCOUNTS.map((account) => (
              <button
                key={account.username}
                type="button"
                onClick={() => {
                  setUsername(account.username);
                  setPassword("password");
                }}
                className="text-left rounded-xl border border-border bg-secondary/60 px-3 py-2 hover:border-primary/40 transition-colors"
              >
                <p className="text-sm font-semibold">{account.role}</p>
                <p className="text-[11px] text-muted-foreground">
                  {account.username}
                </p>
              </button>
            ))}
          </div>
        </div> */}
      </div>
    </div>
  );
}
